import { Chip, Stack } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useMemo } from 'react';

import type { ConnectionStatus } from '../types/gateway-chat-types';

export interface UseGatewayStatusChipsOptions {
  connectionStatus: ConnectionStatus;
  currentRunId: string;
}

export const useGatewayStatusChips = ({
  connectionStatus,
  currentRunId,
}: UseGatewayStatusChipsOptions) => {
  const theme = useTheme();

  return useMemo(() => {
    const statusLabel = connectionStatus.charAt(0).toUpperCase() + connectionStatus.slice(1);

    const statusChipAppearance = (() => {
      switch (connectionStatus) {
        case 'connected':
          return {
            variant: 'outlined' as const,
            sx: {
              fontWeight: 600,
              borderColor: theme.palette.success.main,
              color: theme.palette.success.dark,
              backgroundColor: theme.palette.success.light,
            },
          };
        case 'connecting':
          return {
            variant: 'outlined' as const,
            sx: {
              fontWeight: 600,
              borderColor: theme.palette.warning.main,
              color: theme.palette.warning.dark,
              backgroundColor: theme.palette.warning.light,
            },
          };
        default:
          return {
            variant: 'outlined' as const,
            sx: {
              fontWeight: 600,
              borderColor: theme.palette.grey[300],
              color: theme.palette.text.secondary,
              backgroundColor: theme.palette.grey[100],
            },
          };
      }
    })();

    return (
      <Stack direction="row" spacing={1} alignItems="center">
        <Chip size="small" label={statusLabel} {...statusChipAppearance} />
        {currentRunId ? (
          <Chip
            size="small"
            label={`Run ${currentRunId.substring(0, 8)}`}
            variant="outlined"
            sx={{
              fontWeight: 600,
              borderColor: theme.palette.primary.main,
              color: theme.palette.primary.dark,
              backgroundColor: theme.palette.primary.light,
            }}
          />
        ) : null}
      </Stack>
    );
  }, [connectionStatus, currentRunId, theme]);
};
